/**
 * Global Error Boundary for Yet2Book
 * Catches React errors and reports them to the crash reporting system
 */
import React from 'react';
import { reportCrash } from '../utils/crashReporter';

class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      crashId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidMount() { this.active = true; }
  componentWillUnmount() { this.active = false; }

  async componentDidCatch() {
    // Never retain/log the Error object, props, component stack or user identity.
    try {
      const response = await reportCrash({
        error_type: 'ReactError', severity: 'ERROR', route_or_screen: window.location.pathname,
        metadata: { source: 'GlobalErrorBoundary' },
      });
      if (this.active && response?.success) this.setState({ crashId: response.crash_id });
    } catch { /* The fallback UI remains usable even if telemetry is unavailable. */ }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.iconContainer}>
              <svg
                style={styles.icon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            
            <h1 style={styles.title}>Oops! Something went wrong</h1>
            
            <p style={styles.message}>
              We're sorry for the inconvenience. An unexpected error has occurred.
              Please reload the page. If the problem continues, contact support.
            </p>

            {this.state.crashId && (
              <p style={styles.crashId}>
                Error ID: <code style={styles.code}>{this.state.crashId}</code>
              </p>
            )}

            <div style={styles.buttonContainer}>
              <button onClick={this.handleReload} style={styles.primaryButton}>
                Reload Page
              </button>
              <button onClick={this.handleGoHome} style={styles.secondaryButton}>
                Go to Homepage
              </button>
            </div>

          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f4f6',
    padding: '1rem'
  },
  card: {
    maxWidth: '600px',
    width: '100%',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    padding: '2rem',
    textAlign: 'center'
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1.5rem'
  },
  icon: {
    width: '64px',
    height: '64px',
    color: '#ef4444'
  },
  title: {
    fontSize: '1.875rem',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '1rem'
  },
  message: {
    fontSize: '1rem',
    color: '#6b7280',
    marginBottom: '1.5rem',
    lineHeight: '1.5'
  },
  crashId: {
    fontSize: '0.875rem',
    color: '#9ca3af',
    marginBottom: '1.5rem'
  },
  code: {
    backgroundColor: '#f3f4f6',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    fontFamily: 'monospace',
    fontSize: '0.875rem'
  },
  buttonContainer: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  primaryButton: {
    backgroundColor: '#1B2B4B',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.375rem',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  secondaryButton: {
    backgroundColor: 'white',
    color: '#1B2B4B',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.375rem',
    border: '1px solid #1B2B4B',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  details: {
    marginTop: '2rem',
    textAlign: 'left',
    backgroundColor: '#fef2f2',
    padding: '1rem',
    borderRadius: '0.375rem',
    border: '1px solid #fecaca'
  },
  summary: {
    cursor: 'pointer',
    fontWeight: '500',
    color: '#dc2626',
    marginBottom: '0.5rem'
  },
  errorDetails: {
    fontSize: '0.75rem',
    color: '#991b1b',
    overflow: 'auto',
    maxHeight: '300px',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  }
};

export default GlobalErrorBoundary;

